// src/contexts/SocketContext.tsx
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuth } from './AuthContext';

interface SocketContextType {
  connection: signalR.HubConnection | null;
  isConnected: boolean;
  joinChat: (chatId: string) => void;
  leaveChat: (chatId: string) => void;
  sendMessage: (chatId: string, content: string, type?: string, replyToId?: string) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { token } = useAuth();
  const effectCancelledRef = useRef(false);

  useEffect(() => {
    if (!token) return;

    effectCancelledRef.current = false;

    // Normalize base URL - remove any trailing /api/v1 and add /api/v1/chat
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
    const normalizedBaseUrl = baseUrl.replace(/\/api\/v1\/?$/, '').replace(/\/$/, '');
    const hubUrl = `${normalizedBaseUrl}/api/v1/chat`;

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => token,
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.elapsedMilliseconds < 60000) {
            return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
          }
          return 60000;
        }
      })
      .withServerTimeout(60000)
      .withKeepAliveInterval(15000)
      .configureLogging(signalR.LogLevel.Information)
      .build();

    newConnection.onclose(() => {
      if (!effectCancelledRef.current) setIsConnected(false);
      console.log('Disconnected from SignalR hub');
    });

    newConnection.onreconnecting(() => {
      if (!effectCancelledRef.current) setIsConnected(false);
      console.log('Reconnecting to SignalR hub...');
    });

    newConnection.onreconnected(() => {
      if (!effectCancelledRef.current) setIsConnected(true);
      console.log('Reconnected to SignalR hub');
    });

    newConnection.start()
      .then(() => {
        if (!effectCancelledRef.current) {
          setIsConnected(true);
          setConnection(newConnection);
          console.log('Connected to SignalR hub');
        } else {
          // Cleanup ran before start() completed; stop the connection now to avoid leaks
          newConnection.stop().catch(() => {});
        }
      })
      .catch(err => {
        if (!effectCancelledRef.current) {
          const isStoppedBeforeStart = /stop\(\) was called|AbortError/i.test(err?.message || String(err));
          if (!isStoppedBeforeStart) {
            console.error('Error connecting to SignalR hub:', err);
          }
          setIsConnected(false);
        }
      });

    setConnection(newConnection);

    return () => {
      effectCancelledRef.current = true;
      // Only call stop() if connection has already started; otherwise start()'s then/catch will clean up
      const state = newConnection.state;
      if (state === signalR.HubConnectionState.Connected || state === signalR.HubConnectionState.Reconnecting) {
        newConnection.stop().catch(() => {});
      }
    };
  }, [token]);

  const joinChat = useCallback((chatId: string) => {
    if (connection && isConnected) {
      connection.invoke('JoinChat', chatId).catch(err => {
        console.error('Error joining chat:', err);
      });
    }
  }, [connection, isConnected]);

  const leaveChat = useCallback((chatId: string) => {
    if (connection && isConnected) {
      connection.invoke('LeaveChat', chatId).catch(err => {
        console.error('Error leaving chat:', err);
      });
    }
  }, [connection, isConnected]);

  const sendMessage = useCallback((chatId: string, content: string, type = 'text', replyToId?: string) => {
    if (connection && isConnected) {
      connection.invoke('SendMessage', { chatId, content, type, replyToId }).catch(err => {
        console.error('Error sending message:', err);
      });
    }
  }, [connection, isConnected]);

  const value: SocketContextType = {
    connection,
    isConnected,
    joinChat,
    leaveChat,
    sendMessage,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SignalRProvider');
  }
  return context;
};